import { Outlet, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

import Path from "./LocalRoutes";
import {
  selectContract,
  selectContractLoaded,
} from "../features/capabilities/capabilitySlice";
import { hasCapability } from "../features/capabilities/contract";

/**
 * Route guard, gating on capabilities rather than a role string (ECH-288).
 *
 * ## What changed
 *
 * This read `localStorage.role` and checked it with `String.includes`. The value
 * is built by `role.join("_")`, so a dual-role member is the literal
 * `"mentee_mentor"` and `"mentee_mentor".includes("mentor")` decided what they
 * could reach. Substring matching standing in for authorization, on a value the
 * member can edit in devtools.
 *
 * Capabilities come from the server, are compared exactly, and are reloaded per
 * session rather than baked into a token.
 *
 * ## This is UX, not security
 *
 * The server enforces every one of these. The guard exists so a member is not
 * shown a page whose contents will fail to load, and must never be treated as
 * the actual check.
 *
 * ## What `allowedRoles` was actually doing
 *
 * Every route passed the same `["mentee","mentor","sponsor"]`, which reads as a
 * signed-in check. It was not one: an admin's role string is `"admin"`, which
 * matches none of the three, so that list was what kept admins out of member
 * routes — the only thing it expressed, stated in a form that hid it.
 *
 * It is gone. The server withholds `platform:access` from admins (ECH-174), so
 * every member route now requires that capability and the exclusion is explicit
 * rather than a side effect of which strings happen not to match.
 */
const ProtectedRoute = ({ children, requiredCapability }) => {
  const token = localStorage.getItem("token");
  const contract = useSelector(selectContract);
  const loaded = useSelector(selectContractLoaded);

  if (!token || !token.length) {
    return <Navigate to={Path.Root} replace />;
  }

  // Render nothing while the answer is in flight. Redirecting first would bounce
  // a member out of a page they are allowed to see, which looks like a bug and
  // loses whatever they were doing.
  if (requiredCapability && !loaded) return null;

  if (requiredCapability && !hasCapability(contract, requiredCapability)) {
    return <Navigate to={Path.Root} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
